import { apiErrorSchema, fieldIssueSchema, type FieldIssue } from '@/shared/types/api';
import { z } from 'zod';

/**
 * Erro normalizado da API, pronto para virar toast ou mensagem de campo.
 *
 * Toda falha de rede vira um `ApiError` — inclusive queda de conexao e resposta
 * fora do contrato. A tela nunca precisa inspecionar `AxiosError`.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number | null;
  readonly correlationId: string | null;
  readonly fieldIssues: FieldIssue[];
  /**
   * `details` cru da resposta.
   *
   * Nem todo `details` e uma lista de erros de campo: o `429` traz `retryAfter` e
   * `blockMinutes`, e o `401` de login traz `attemptsRemaining`. Cada consumidor
   * valida o formato que espera com o proprio schema.
   */
  readonly details: unknown;

  constructor(params: {
    code: string;
    message: string;
    status?: number | null;
    correlationId?: string | null;
    fieldIssues?: FieldIssue[];
    details?: unknown;
    cause?: unknown;
  }) {
    super(params.message, { cause: params.cause });
    this.name = 'ApiError';
    this.code = params.code;
    this.status = params.status ?? null;
    this.correlationId = params.correlationId ?? null;
    this.fieldIssues = params.fieldIssues ?? [];
    this.details = params.details;
  }

  /** `true` quando a sessao expirou ou foi revogada. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** `true` quando o servidor devolveu erros por campo. */
  get hasFieldIssues(): boolean {
    return this.fieldIssues.length > 0;
  }
}

const NETWORK_MESSAGE =
  'Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.';
const UNEXPECTED_MESSAGE = 'Não foi possível concluir a operação. Tente novamente em instantes.';

/**
 * Converte qualquer falha numa instancia de `ApiError`.
 *
 * @param error Erro capturado pelo interceptor.
 * @param status Status HTTP, quando houve resposta.
 * @param body Corpo da resposta, quando houve.
 */
export function toApiError(error: unknown, status: number | null, body: unknown): ApiError {
  if (status === null) {
    return new ApiError({ code: 'NETWORK_ERROR', message: NETWORK_MESSAGE, status: null, cause: error });
  }

  const parsed = apiErrorSchema.safeParse(body);
  if (!parsed.success) {
    return new ApiError({ code: 'UNEXPECTED_RESPONSE', message: UNEXPECTED_MESSAGE, status, cause: error });
  }

  const { code, message, details, correlationId } = parsed.data.error;
  const issues = z.array(fieldIssueSchema).safeParse(details);

  return new ApiError({
    code,
    message,
    status,
    correlationId: correlationId ?? null,
    fieldIssues: issues.success ? issues.data : [],
    details,
    cause: error,
  });
}
